import { type FC, useCallback, useEffect, useMemo, useState } from "react";

import { permanentlyDeleteAnIssueWorkspaceAggregateAsUser } from "../../../dashboard-client";
import { useOptionalDashboardAuth } from "../../../hooks/useDashboardAuth";
import { useTeamsChatSdkEnabled } from "../../../hooks/useTeamsChatSdkEnabled";
import { isAdmin } from "../../../utils/rbac";
import { HudStatusPill } from "../../common/HudStatusPill";
import { SideSheet } from "../../common/SideSheet";
import { type Tab, Tabs } from "../../common/Tabs";
import {
  type ActorAttribution,
  type BlackboardChannel,
  type BlackboardInstance,
} from "../blackboardModels";
import { ChannelArtifactsPanel } from "../ChannelArtifactsPanel";
import {
  getStatusLabel,
  getStatusMotion,
  getStatusTone,
} from "../environment/environmentUtils";
import { useWorkspaceController } from "../environment/useWorkspaceController";
import { WorkspaceDialogs } from "../environment/WorkspaceDialogs";
import { InstanceAvatar } from "../InstanceAvatar";
import { PreviewSurface } from "../PreviewDrawer";
import { RetroError } from "../retro";
import { useInstanceStream } from "../useInstanceStream";
import { type OrgChannelSummary } from "../useOrgChannels";
import { WakeDeliveryChip } from "../WakeDeliveryChip";
import { ChannelRail, type RailSection } from "./ChannelRail";
import { ChannelTimeline } from "./ChannelTimeline";
import {
  InspectorToolbar,
  SHEET_WIDTH,
  type BreadcrumbItem,
} from "./InspectorChrome";
import { useChannelMessages } from "./useChannelMessages";

// ---------------------------------------------------------------------------
// ChannelSheet — the channel surface: Chat · Members · Workspace · Artifacts ·
// (Preview). One of the two stable inspector identities (the other is
// ActorSheet). Extracted verbatim from the former InspectorSheet dispatcher so
// the channel mode owns its hooks + chrome directly.
// ---------------------------------------------------------------------------

interface ChannelSheetProps {
  actorAttributions: ActorAttribution[];
  isOpen: boolean;
  channelId: string;
  onClose: () => void;
  ancestors: BreadcrumbItem[];
  onJumpTo: (index: number) => void;
  onInspectActor: (instanceId: string) => void;
  instances: BlackboardInstance[];
  orgChannelsById: Map<string, OrgChannelSummary>;
  onReloadOrgChannels?: () => Promise<boolean>;
  sendMessage: (
    instanceId: string,
    channelId: string,
    message: string,
  ) => Promise<string>;
  onKillInstance: (instanceId: string) => Promise<void>;
  onRefresh: () => void;
  draft: string;
  onDraftChange: (channelId: string, value: string) => void;
  activeTab: string;
  onActiveTabChange: (channelId: string, tab: string) => void;
  selectedArtifactId: string | null;
  onSelectedArtifactChange: (channelId: string, id: string | null) => void;
  /** When false, Escape does not dismiss — set when an overlay sheet covers
   *  this base so Esc dismisses only the topmost. */
  escDismisses?: boolean;
  /** Z-index class; default "z-50", pass "z-[60]" for an overlay. */
  zIndexClass?: string;
  /** Label for the close button. Default "Close"; pass "‹ Back" for an
   *  overlay sheet whose close returns to the base. */
  closeLabel?: string;
}

const IssueWorkspaceDeleteMenu: FC<{
  channelId: string;
  onClose: () => void;
  onRefresh: () => void;
}> = ({ channelId, onClose, onRefresh }) => {
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await permanentlyDeleteAnIssueWorkspaceAggregateAsUser({
        path: { channelId },
      });
      setOpen(false);
      setConfirmDelete(false);
      onRefresh();
      onClose();
    } catch (error) {
      setDeleteError(
        error instanceof Error
          ? error.message
          : "Failed to delete issue workspace.",
      );
    } finally {
      setDeleting(false);
    }
  }, [channelId, onClose, onRefresh]);

  return (
    <div className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Issue workspace actions"
        onClick={() => {
          setDeleteError(null);
          setConfirmDelete(false);
          setOpen((current) => !current);
        }}
        className="flex h-6 w-6 items-center justify-center border border-slate-300 font-mono text-[14px] leading-none text-slate-600 transition-colors hover:border-emerald-400/60 hover:text-emerald-700 dark:border-slate-700 dark:text-slate-400 dark:hover:text-emerald-300"
      >
        ⋮
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 w-72 border border-slate-300 bg-white shadow-[4px_4px_0_rgba(15,23,42,0.18)] dark:border-emerald-400/30 dark:bg-slate-950/95 dark:shadow-[4px_4px_0_rgba(2,6,23,0.6)]"
        >
          <p className="border-b border-slate-200 px-3 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-slate-500 dark:border-slate-800/80">
            [ Issue workspace ]
          </p>
          {!confirmDelete ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => setConfirmDelete(true)}
              className="w-full px-3 py-2 text-left font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-rose-700 transition-colors hover:bg-rose-500/10 dark:text-rose-300"
            >
              Delete workspace…
            </button>
          ) : (
            <div className="flex flex-col gap-2 border-t border-rose-500/30 bg-rose-500/[0.04] p-2">
              <p className="font-mono text-[10px] leading-relaxed text-rose-700 dark:text-rose-200">
                Stops the workspace, marks its actors and channel deleted, and
                retains the channel history for authorized review.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  disabled={deleting}
                  onClick={() => setConfirmDelete(false)}
                  className="border border-slate-300 px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-slate-600 disabled:opacity-50 dark:border-slate-700 dark:text-slate-400"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={deleting}
                  onClick={() => void handleDelete()}
                  className="border border-rose-500/50 bg-rose-500/5 px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.18em] text-rose-700 disabled:opacity-50 dark:border-rose-500/40 dark:text-rose-300"
                >
                  {deleting ? "Deleting…" : "Confirm delete"}
                </button>
              </div>
            </div>
          )}
          {deleteError && (
            <p className="border-t border-rose-500/30 px-3 py-1.5 font-mono text-[9px] uppercase tracking-widest text-rose-600 dark:text-rose-300">
              {deleteError}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export const ChannelSheet: FC<ChannelSheetProps> = ({
  actorAttributions,
  isOpen,
  channelId,
  onClose,
  ancestors,
  onJumpTo,
  onInspectActor,
  instances,
  orgChannelsById,
  onReloadOrgChannels,
  sendMessage,
  onKillInstance,
  onRefresh,
  draft,
  onDraftChange,
  activeTab,
  onActiveTabChange,
  selectedArtifactId,
  onSelectedArtifactChange,
  escDismisses = true,
  zIndexClass = "z-50",
  closeLabel = "Close",
}) => {
  // Presence rail: null = auto (visible lg+, hidden below), true/false = the
  // operator's explicit toggle via the header avatar cluster. The toggle flips
  // EFFECTIVE visibility — on a wide viewport the rail is already showing
  // under `null`, so the first click must hide it, not "enable" a no-op.
  const [railVisible, setRailVisible] = useState<boolean | null>(null);
  // The sheet is capped at max-w-5xl (1024px), so viewport width was never the
  // right question: however wide the window is, the rail shares at most 1024px
  // with the timeline. Docking the rail in flow keeps both integral -- the
  // timeline squeezes instead of being covered -- and at any sheet width above
  // ~640px there is room for both (1024 - 240 rail - 28 handle leaves ~756px of
  // chat). Only a genuinely tiny sheet cannot afford 240px beside the chat, so
  // the overlay survives as a narrow-screen exception rather than the rule.
  const [isTightViewport, setIsTightViewport] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 639px)").matches,
  );
  useEffect(() => {
    const query = window.matchMedia("(max-width: 639px)");
    const onChange = (event: MediaQueryListEvent) =>
      setIsTightViewport(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  const railEffectivelyVisible = railVisible ?? !isTightViewport;
  const [killingInstanceId, setKillingInstanceId] = useState<string | null>(
    null,
  );
  const handleDraftChange = useCallback(
    (value: string) => onDraftChange(channelId, value),
    [onDraftChange, channelId],
  );
  const handleTabChange = useCallback(
    (tab: string) => onActiveTabChange(channelId, tab),
    [onActiveTabChange, channelId],
  );
  const handleArtifactChange = useCallback(
    (id: string | null) => onSelectedArtifactChange(channelId, id),
    [onSelectedArtifactChange, channelId],
  );

  const members = useMemo(
    () =>
      instances.filter((instance) =>
        instance.channels.some((c) => c.id === channelId),
      ),
    [instances, channelId],
  );
  const channel = orgChannelsById.get(channelId) ?? null;
  const teamsChatSdk = useTeamsChatSdkEnabled();
  const memberChannel =
    members[0]?.channels.find((candidate) => candidate.id === channelId) ??
    null;
  const displayChannel = channel ?? memberChannel;
  const channelDisplayName = displayChannel?.displayName ?? "…";
  // Channel kind drives the tab set: private channels are 1:1 conversations
  // (Timeline only); group channels are rooms (Timeline + Members + Workspace
  // + Artifacts + Preview). Falls back to shared when the kind is unknown —
  // the historical behavior — so a missing summary doesn't lose tabs.
  const channelKind: "private" | "shared" =
    displayChannel?.kind === "private" ? "private" : "shared";
  const isPrivate = channelKind === "private";
  const auth = useOptionalDashboardAuth();
  const canDeleteIssueWorkspace =
    !isPrivate &&
    displayChannel?.provenance === "issue_workspace" &&
    auth !== null &&
    isAdmin(auth.orgRole, auth.globalRole);

  // Proxy through the OLDEST member so the instance-scoped history endpoint
  // surfaces the most messages (see useChannelMessages).
  const proxyInstance: BlackboardInstance | null = useMemo(
    () =>
      members.length === 0
        ? null
        : members.toSorted((a, b) => a.createdAt.localeCompare(b.createdAt))[0],
    [members],
  );
  const proxyInstanceId = proxyInstance?.id ?? null;

  const roomChannel: BlackboardChannel = useMemo(
    () => ({
      displayName: channelDisplayName,
      id: channelId,
      kind: channelKind,
      ownerActorId: null,
      provenance: displayChannel?.provenance ?? "manual",
    }),
    [channelDisplayName, channelId, channelKind, displayChannel?.provenance],
  );
  // One workspace controller per sheet: the header env chip, the rail's
  // management section, the Preview tab, and the modal set all share it.
  const workspace = useWorkspaceController(roomChannel);
  const { environment, launchPreview } = workspace;
  const hasPreview = (environment?.previewPorts.length ?? 0) > 0;
  // The rail's active accordion section lives here (not in ChannelRail) so it
  // survives rail hide/show and tab switches; the sheet is keyed by channelId,
  // which resets it per channel. A memberless channel opens on WORKSPACE (the
  // only actionable section), and when the last member leaves the rail follows
  // — but returning members never yank the operator away from WORKSPACE.
  const memberless = members.length === 0;
  const [activeRailSection, setActiveRailSection] = useState<RailSection>(
    memberless ? "workspace" : "members",
  );
  useEffect(() => {
    if (memberless) {
      setActiveRailSection("workspace");
    }
  }, [memberless]);
  const visibleHeaderMembers = members.slice(0, 4);
  const hiddenHeaderMemberCount = Math.max(
    0,
    members.length - visibleHeaderMembers.length,
  );

  const {
    events,
    liveSince,
    messages: liveMessages,
  } = useInstanceStream(proxyInstanceId, [channelId]);
  const {
    actorAttributions: historyActorAttributions,
    appendLocalUserMessage,
    hasMore,
    historyError,
    loadingMore,
    loadMore,
    messages,
  } = useChannelMessages(
    channelId,
    proxyInstanceId,
    liveMessages,
    actorAttributions,
  );
  const timelineActorAttributions = useMemo(() => {
    const byId = new Map(
      actorAttributions.map((attribution) => [attribution.id, attribution]),
    );
    for (const attribution of historyActorAttributions) {
      byId.set(attribution.id, attribution);
    }
    return [...byId.values()];
  }, [actorAttributions, historyActorAttributions]);

  const handleSendMessage = useCallback(
    async (targetChannelId: string, message: string) => {
      if (proxyInstanceId === null) {
        throw new Error("No channel actor is available to proxy this send.");
      }
      return sendMessage(proxyInstanceId, targetChannelId, message);
    },
    [proxyInstanceId, sendMessage],
  );
  const handleKillMember = useCallback(
    async (instanceId: string) => {
      setKillingInstanceId(instanceId);
      try {
        await onKillInstance(instanceId);
      } finally {
        setKillingInstanceId(null);
      }
    },
    [onKillInstance],
  );
  // Tabs adapt to channel kind. Private channels are a 1:1 conversation —
  // Timeline only. Group channels are rooms with members, workspace,
  // artifacts, and preview. Timeline is the default in both; Chat was dropped
  // (it was a strict subset of Timeline — same messages, no activity).
  // Members is presence, not a place: the roster lives as a rail docked on
  // the Timeline (the header avatar cluster toggles it), not as its own tab.
  const tabs: Tab[] = isPrivate
    ? [{ id: "timeline", label: "Timeline" }]
    : [
        { id: "timeline", label: "Timeline" },
        { id: "artifacts", label: "Artifacts" },
        { disabled: !hasPreview, id: "preview", label: "Preview" },
      ];
  // Fall back to Timeline when the selected tab doesn't exist for this kind
  // (e.g. Preview selected then switched to a private channel).
  const tabIds = new Set(tabs.map((t) => t.id));
  const effectiveTab = tabIds.has(activeTab) ? activeTab : "timeline";

  return (
    <SideSheet
      isOpen={isOpen}
      onClose={onClose}
      title={channelDisplayName}
      widthClass={SHEET_WIDTH}
      escDismisses={escDismisses}
      zIndexClass={zIndexClass}
      closeLabel={closeLabel}
      headerContent={
        <div className="relative flex min-w-0 flex-wrap items-center gap-2">
          <span aria-hidden className="text-emerald-500 dark:text-emerald-400">
            #
          </span>
          <h3 className="truncate font-mono text-[13px] font-bold uppercase tracking-widest text-slate-900 dark:text-white">
            {channelDisplayName}
          </h3>
          {/* Rail toggle — a permanent affordance on shared channels (the rail
              is the channel's control surface, so it must stay reachable with
              zero members); omitted entirely on private 1:1s, which have no
              rail. Avatars are supplemental content inside the button, with a
              panel glyph as the memberless fallback. */}
          {!isPrivate && (
            <button
              type="button"
              onClick={() => setRailVisible(!railEffectivelyVisible)}
              aria-pressed={railEffectivelyVisible}
              aria-label="Toggle channel rail"
              title="Toggle channel rail"
              className="inline-flex items-center"
            >
              {members.length === 0 ? (
                <span
                  aria-hidden
                  className="flex h-3.5 w-4 items-stretch border border-slate-400 dark:border-slate-600"
                >
                  <span className="ml-auto w-1.5 border-l border-slate-400 bg-slate-200 dark:border-slate-600 dark:bg-slate-700" />
                </span>
              ) : (
                <span className="flex -space-x-1">
                  {visibleHeaderMembers.map((member) => (
                    <span
                      key={member.id}
                      className="border border-white bg-white dark:border-slate-950 dark:bg-slate-950"
                    >
                      <InstanceAvatar seed={member.avatarSeed} size="xs" />
                    </span>
                  ))}
                </span>
              )}
              {hiddenHeaderMemberCount > 0 && (
                <span className="ml-1 font-mono text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-500">
                  +{hiddenHeaderMemberCount}
                </span>
              )}
            </button>
          )}
          <WakeDeliveryChip summary={channel} />
          <HudStatusPill
            label={
              environment === null
                ? "env none"
                : `env ${getStatusLabel(environment)}`
            }
            tone={getStatusTone(environment?.status ?? null)}
            signature={getStatusMotion(environment?.status ?? null)}
            hideDot={environment == null}
          />
          <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-500">
            {isPrivate
              ? "private"
              : `shared · ${members.length} ${members.length === 1 ? "member" : "members"}`}
          </span>
          {canDeleteIssueWorkspace && (
            <IssueWorkspaceDeleteMenu
              channelId={channelId}
              onClose={onClose}
              onRefresh={onRefresh}
            />
          )}
        </div>
      }
    >
      <div className="-mx-3 -my-2.5 flex h-[calc(100%+1.25rem)] flex-col sm:-mx-4 sm:-my-3 sm:h-[calc(100%+1.5rem)]">
        <InspectorToolbar
          ancestors={ancestors}
          currentLabel={`#${channelDisplayName}`}
          onJumpTo={onJumpTo}
        />
        <Tabs
          tabs={tabs}
          activeTab={effectiveTab}
          onChange={handleTabChange}
          ariaLabel="Channel inspector sections"
        />
        <div className="min-h-0 min-w-0 flex-1">
          {/* Timeline: merged messages + activity, with the members/workspace
              rail docked right in flow (the edge handle opens and closes it, so
              nothing is hidden behind a header glyph). The rail only overlays on
              a tight sheet, where 240px beside the chat would strangle it. */}
          {effectiveTab === "timeline" && (
            <div className="relative flex h-full min-h-0 min-w-0">
              <div className="min-w-0 flex-1">
                {historyError && <ChannelHistoryError message={historyError} />}
                <ChannelTimeline
                  actorAttributions={timelineActorAttributions}
                  channelId={channelId}
                  appendLocalUserMessage={appendLocalUserMessage}
                  draft={draft}
                  events={events}
                  hasMore={hasMore}
                  liveSince={liveSince}
                  loadMore={loadMore}
                  loadingMore={loadingMore}
                  members={members}
                  messages={messages}
                  onDraftChange={handleDraftChange}
                  onSendMessage={handleSendMessage}
                  readOnly={proxyInstance === null}
                />
              </div>
              {/* Persistent drawer handle. The rail's existence must not depend on
                  discovering that a header glyph is a button, so an always-present
                  edge strip advertises it: closed it reads as a grip with the
                  section names, open it reads as a collapse control. It sits at the
                  rail boundary in both states, so the grip never moves. z-20 keeps
                  it above the narrow-viewport overlay rail (z-10), which docks
                  against it rather than under it. */}
              {!isPrivate && (
                <button
                  type="button"
                  onClick={() => setRailVisible(!railEffectivelyVisible)}
                  aria-expanded={railEffectivelyVisible}
                  aria-label={
                    railEffectivelyVisible
                      ? "Collapse channel panel"
                      : "Expand channel panel"
                  }
                  title={
                    railEffectivelyVisible
                      ? "Collapse panel"
                      : "Expand panel (members and workspace)"
                  }
                  className="relative z-20 flex w-7 shrink-0 flex-col items-center gap-3 border-l border-slate-200 bg-slate-50 py-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
                >
                  <span
                    aria-hidden
                    className="font-mono text-[12px] leading-none"
                  >
                    {railEffectivelyVisible ? "\u203a" : "\u2039"}
                  </span>
                  {!railEffectivelyVisible && (
                    <span
                      aria-hidden
                      className="font-mono text-[9px] font-bold uppercase tracking-[0.2em]"
                      style={{ writingMode: "vertical-rl" }}
                    >
                      {[
                        memberless ? null : `members ${members.length}`,
                        "workspace",
                        teamsChatSdk.enabled ? "connections" : null,
                      ]
                        .filter((part) => part !== null)
                        .join(" \u00b7 ")}
                    </span>
                  )}
                </button>
              )}
              {!isPrivate && railEffectivelyVisible && (
                <aside
                  className={`${
                    isTightViewport
                      ? "absolute inset-y-0 right-7 z-10 shadow-[-6px_0_16px_rgba(2,6,23,0.25)]"
                      : ""
                  } flex w-60 shrink-0 flex-col border-l border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950`}
                >
                  <ChannelRail
                    members={members}
                    killingInstanceId={killingInstanceId}
                    channel={channel}
                    teamsEnabled={teamsChatSdk.enabled}
                    teamsIsAdmin={teamsChatSdk.isAdmin}
                    teamsBotReady={teamsChatSdk.botReady}
                    onReloadChannels={onReloadOrgChannels}
                    activeSection={activeRailSection}
                    onSectionChange={setActiveRailSection}
                    onInspectActor={onInspectActor}
                    onKill={(instanceId) => void handleKillMember(instanceId)}
                    onLifecycleChange={onRefresh}
                    workspace={workspace}
                  />
                </aside>
              )}
            </div>
          )}

          {/* Artifacts are channel-owned, so this tab no longer depends on a
              proxy actor: a memberless, archived, or retained-history channel
              still renders its own record (read-only — there is no
              channel-scoped write route). Previously it showed "spawn an actor
              first", which made the durable record look like it died with the
              last actor that wrote it. */}
          {effectiveTab === "artifacts" && (
            <div className="min-h-0 flex-1 overflow-hidden p-3">
              <ChannelArtifactsPanel
                channels={[roomChannel]}
                selectedChannelId={channelId}
                initialSelectedArtifactId={selectedArtifactId}
                onSelectedArtifactChange={handleArtifactChange}
              />
            </div>
          )}

          {effectiveTab === "preview" && (
            <PreviewSurface
              closeOnEscape={false}
              onClose={() => handleTabChange("timeline")}
              environment={environment}
              launchPreview={launchPreview}
              title={channelDisplayName}
            />
          )}
          {!isPrivate && <WorkspaceDialogs controller={workspace} />}
        </div>
      </div>
    </SideSheet>
  );
};

interface ChannelHistoryErrorProps {
  message: string;
}

function ChannelHistoryError({ message }: ChannelHistoryErrorProps) {
  return (
    <div className="p-3">
      <RetroError message={message} />
    </div>
  );
}
